<template>
  <div class="flex items-center justify-between py-4 px-6 bg-background border-b">
    <!-- Left: Back button with type chiplet -->
    <div class="flex items-center space-x-2">
      <Button variant="ghost" size="icon" title="Go back" @click="goBack">
        <Icon name="heroicons:arrow-left" class="h-4 w-4" />
        <span class="sr-only">Go back</span>
      </Button>
      <div
        v-if="bubbleType"
        class="flex items-center space-x-1 px-2 py-1 rounded-full text-xs"
        :style="typeChipStyle"
      >
        <Icon :name="bubbleType.icon" class="h-3 w-3" />
        <span>{{ bubbleType.name }}</span>
      </div>
    </div>

    <!-- Middle: Bubble title -->
    <h1 class="text-xl font-bold truncate max-w-md" :title="bubble.title">
      {{ bubble.title }}
    </h1>

    <!-- Right: Action buttons -->
    <div class="flex items-center space-x-2">
      <!-- Hidden chiplet -->
      <div v-if="bubble.hidden" class="bg-gray-500 text-white text-xs px-2 py-1 rounded-full mr-2">
        Hidden
      </div>

      <!-- Archived chiplet -->
      <div
        v-if="bubble.archived"
        class="bg-blue-500 text-white text-xs px-2 py-1 rounded-full mr-2"
      >
        Archived
      </div>
      <div v-if="bubble.part" class="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full mr-2">
        Partial
      </div>

      <div
        v-if="!bubble.processed"
        class="bg-red-500 text-white text-xs px-2 py-1 rounded-full mr-2"
      >
        Processing
      </div>

      <!-- Manage notes -->
      <Button variant="ghost" size="icon" title="Open chat" @click="openChatModal">
        <Icon name="heroicons:chat-bubble-left-right" class="h-4 w-4" />
        <span class="sr-only">Open chat</span>
      </Button>

      <!-- Insights -->
      <Button
        v-if="userStore.hasInsightsAccess"
        variant="ghost"
        size="icon"
        title="View insights"
        @click="navigateToInsights"
      >
        <Icon name="heroicons:light-bulb" class="h-4 w-4" />
        <span class="sr-only">View insights</span>
      </Button>

      <!-- Add to collection -->
      <Popover>
        <PopoverTrigger as-child>
          <Button variant="ghost" size="icon" title="Add bubble to collection">
            <Icon name="heroicons:square-3-stack-3d" class="h-4 w-4" />
            <span class="sr-only">Add bubble to collection</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-56">
          <div class="space-y-2">
            <h3 class="font-medium text-sm">Add to Collection</h3>
            <div v-if="collectionsLoading" class="text-center py-2">
              <Icon name="heroicons:arrow-path" class="h-5 w-5 animate-spin" />
            </div>
            <div
              v-else-if="collections.length === 0"
              class="text-center py-2 text-sm text-gray-500"
            >
              No collections available
            </div>
            <ul v-else class="max-h-48 overflow-auto">
              <li v-for="collection in collections" :key="collection.uuid" class="py-1">
                <Button
                  variant="ghost"
                  class="w-full justify-start items-center"
                  @click="addToCollection(collection.uuid)"
                >
                  <Icon
                    v-if="collection.bubbles?.some((b) => b.uuid === bubble.uuid)"
                    name="heroicons:check"
                    class="h-4 w-4 text-primary mr-2 flex-shrink-0"
                  />
                  <span class="truncate">{{ collection.title }}</span>
                </Button>
              </li>
            </ul>
          </div>
        </PopoverContent>
      </Popover>

      <!-- Review/Unreview -->
      <Button
        variant="ghost"
        size="icon"
        :title="bubble.reviewed ? 'Mark as unreviewed' : 'Mark as reviewed'"
        @click="toggleReviewed"
      >
        <Icon
          :name="bubble.reviewed ? 'heroicons:check-circle' : 'heroicons:check-circle'"
          :class="['h-4 w-4', bubble.reviewed ? 'text-green-500' : '']"
        />
        <span class="sr-only">{{
          bubble.reviewed ? 'Mark as unreviewed' : 'Mark as reviewed'
        }}</span>
      </Button>

      <!-- Manage notes -->
      <Button variant="ghost" size="icon" title="Open notes" @click="openNoteModal">
        <Icon name="heroicons:pencil-square" class="h-4 w-4" />
        <span class="sr-only">Open notes</span>
      </Button>

      <!-- More options dropdown -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" size="icon" title="Show more options">
            <Icon name="heroicons:ellipsis-vertical" class="h-4 w-4" />
            <span class="sr-only">Show more options</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            v-for="item in dropdownItems"
            :key="item.label"
            :disabled="item.disabled?.value"
            @select="item.click"
          >
            <Icon
              :name="item.icon"
              class="mr-2 h-4 w-4"
              :class="{ 'animate-spin': item.loading?.value }"
            />
            <span>{{ item.loading?.value ? item.loadingLabel : item.label }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
  <ChangeTitlePopup
    v-model:is-open="isTitleChangePopupOpen"
    :bubble-uuid="bubble.uuid"
    :current-title="bubble.title"
    @title-changed="handleTitleChanged"
  />
  <NoteModal v-if="isNoteModalOpen" :bubble-uuid="bubble.uuid" @close="closeNoteModal" />
  <ChatModal v-model:is-open="showChat" :bubble-uuid="bubble.uuid" :title="bubble.title" />

  <!-- Delete Confirmation Dialog -->
  <AlertDialog v-model:open="isDeleteDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
        <AlertDialogDescription>
          This action cannot be undone. This will permanently delete the bubble "<strong>{{
            localBubble.title
          }}</strong
          >".
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel :disabled="isDeleting">Cancel</AlertDialogCancel>
        <AlertDialogAction :disabled="isDeleting" @click="confirmDeleteBubble">
          <Icon v-if="isDeleting" name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
          {{ isDeleting ? 'Deleting...' : 'Confirm Delete' }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, type Ref, h } from 'vue'
import type { Bubble, AnyBubble, BubbleType } from '@/stores/interfaces'
import { useBubbleStore } from '@/stores/bubble'
import { useBubblesStore } from '@/stores/bubbles'
import { useCollectionsStore } from '@/stores/collections'
import type { CollectionResponse } from '~/types/collections'
import { useUserShareStore } from '~/stores/userShare'
import { useUserStore } from '@/stores/user'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import ChangeTitlePopup from '@/components/bubbles/ChangeTitlePopup.vue'
import NoteModal from '@/components/notes/NoteModal.vue'
import { useToast } from '@/components/ui/toast/use-toast'
import { useRouter } from 'vue-router'
import ChatModal from '@/components/chat/ChatModal.vue'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface BubbleWithOffline extends Bubble {
  offline?: boolean
}

const props = defineProps<{
  bubble: Bubble | AnyBubble
}>()

const emit = defineEmits<{
  'update:bubble': [updatedBubble: Bubble | AnyBubble]
}>()

const localBubble = ref<BubbleWithOffline>(props.bubble as BubbleWithOffline)

const bubbleStore = useBubbleStore()
const bubblesStore = useBubblesStore()
const collectionsStore = useCollectionsStore()
const userShareStore = useUserShareStore()
const userStore = useUserStore()
const router = useRouter()

const goBack = () => router.back()

const bubbleType = computed<BubbleType | undefined>(
  () => bubblesStore.bubbleTypes[localBubble.value.type]
)
const typeChipStyle = computed(() => {
  if (bubbleType.value) {
    return {
      backgroundColor: `${bubbleType.value.color}20`,
      color: bubbleType.value.color,
    }
  }
  return {}
})

const collections = ref<CollectionResponse[]>([])
const collectionsLoading = ref(false)

onMounted(async () => {
  await fetchCollections()
})

const fetchCollections = async () => {
  collectionsLoading.value = true
  try {
    const fetchedCollections = await collectionsStore.fetchCollections()
    // Sort collections by updatedAt in descending order (most recent first)
    // If updatedAt is not present, use createdAt.
    collections.value = fetchedCollections.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime()
      const dateB = new Date(b.updatedAt || b.createdAt).getTime()
      return dateB - dateA
    })
  } catch (error) {
    logError('Error fetching collections:', error)
    toast({
      title: 'Error',
      description: 'Failed to fetch collections',
      variant: 'destructive',
    })
  } finally {
    collectionsLoading.value = false
  }
}

const addToCollection = async (collectionUuid: string) => {
  try {
    const result = await collectionsStore.addBubbleToCollection(collectionUuid, [
      localBubble.value.uuid,
    ])
    if (result) {
      toast({
        title: 'Success',
        description: 'Bubble added to collection successfully',
      })
    } else {
      throw new Error('Failed to add bubble to collection')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to add bubble to collection',
      variant: 'destructive',
    })
  }
}

const toggleArchive = async () => {
  isTogglingArchive.value = true
  try {
    const wasArchived = localBubble.value.archived
    const result = await bubbleStore.toggleArchive(localBubble.value.uuid)
    if (result.success) {
      toast({
        title: 'Success',
        description: `Bubble ${wasArchived ? 'unarchived' : 'archived'}`,
      })
    } else {
      throw new Error(result.error || 'Failed to toggle archive state')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to toggle archive state',
      variant: 'destructive',
    })
  } finally {
    isTogglingArchive.value = false
  }
}

const toggleHidden = async () => {
  isTogglingHidden.value = true
  try {
    const result = await bubbleStore.toggleHidden(localBubble.value.uuid)
    if (result.success) {
      toast({
        title: 'Success',
        description: `Bubble ${localBubble.value.hidden ? 'unhidden' : 'hidden'}`,
      })
    } else {
      throw new Error(result.error || 'Failed to toggle hidden state')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to toggle hidden state',
      variant: 'destructive',
    })
  } finally {
    isTogglingHidden.value = false
  }
}

const toggleReviewed = async () => {
  try {
    const result = await bubbleStore.toggleReviewed(localBubble.value.uuid)
    if (result.success) {
      toast({
        title: 'Success',
        description: `Bubble marked as ${localBubble.value.reviewed ? 'reviewed' : 'unreviewed'}`,
      })
    } else {
      throw new Error(result.error || 'Failed to toggle reviewed state')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to toggle reviewed state',
      variant: 'destructive',
    })
  }
}

const isDeleting = ref(false)
const isDeleteDialogOpen = ref(false)

const deleteBubble = async () => {
  isDeleteDialogOpen.value = true
}

const confirmDeleteBubble = async () => {
  isDeleting.value = true
  try {
    // Navigate home first to avoid any 404 flashes
    router.push('/dashboard/home')

    const result = await bubbleStore.deleteBubble(localBubble.value.uuid)
    if (result.success) {
      toast({
        title: 'Success',
        description: result.message,
        variant: 'default',
      })
    } else {
      throw new Error(result.error || 'Failed to delete bubble')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to delete bubble',
      variant: 'destructive',
    })
  } finally {
    isDeleting.value = false
    isDeleteDialogOpen.value = false
  }
}

const { toast } = useToast()

const refreshBubbleMetadata = async () => {
  isRefreshingMeta.value = true
  try {
    const result = await bubbleStore.refreshBubbleMetadata(localBubble.value.uuid)
    if (result.success) {
      const updatedBubble = await bubblesStore.fetchBubble(localBubble.value.uuid)
      if (!updatedBubble) {
        throw new Error('Failed to fetch updated bubble')
      }
      localBubble.value = updatedBubble as BubbleWithOffline
      emit('update:bubble', updatedBubble)
      toast({
        title: 'Success',
        description: 'Bubble metadata refreshed successfully',
      })
    } else {
      throw new Error(result.error || 'Failed to refresh bubble metadata')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to refresh bubble metadata',
      variant: 'destructive',
    })
  } finally {
    isRefreshingMeta.value = false
  }
}

const refreshBubbleFull = async () => {
  isRebuilding.value = true
  try {
    const result = await bubbleStore.refreshBubbleFull(localBubble.value.uuid)
    if (result.success) {
      const updatedBubble = await bubblesStore.fetchBubble(localBubble.value.uuid)
      if (!updatedBubble) {
        throw new Error('Failed to fetch updated bubble')
      }
      localBubble.value = updatedBubble as BubbleWithOffline
      emit('update:bubble', updatedBubble)
      toast({
        title: 'Success',
        description: 'Full bubble refresh completed successfully',
      })
    } else {
      throw new Error(result.error || 'Failed to perform full bubble refresh')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to perform full bubble refresh',
      variant: 'destructive',
    })
  } finally {
    isRebuilding.value = false
  }
}

const toggleOffline = async () => {
  isTogglingOffline.value = true
  try {
    let result

    if (!localBubble.value) {
      throw new Error('Bubble data is undefined')
    }

    if ((localBubble.value as BubbleWithOffline).offline) {
      result = await bubbleStore.removeDownload(localBubble.value.uuid)
    } else {
      result = await bubbleStore.downloadBubble(localBubble.value.uuid)
    }

    if (result.success) {
      try {
        const updatedBubble = await bubblesStore.fetchBubble(localBubble.value.uuid)

        if (!updatedBubble) {
          throw new Error('Failed to fetch updated bubble data')
        }

        localBubble.value = updatedBubble as BubbleWithOffline
        emit('update:bubble', updatedBubble)

        toast({
          title: 'Success',
          description: (updatedBubble as BubbleWithOffline).offline
            ? 'Bubble downloaded successfully'
            : 'Bubble download removed successfully',
          variant: 'default',
        })
      } catch (fetchError) {
        logError('Error fetching updated bubble:', fetchError)
        // If we can't fetch the updated bubble, we'll update the offline status manually
        localBubble.value = {
          ...localBubble.value,
          offline: !(localBubble.value as BubbleWithOffline).offline,
        }
        emit('update:bubble', localBubble.value)

        toast({
          title: 'Partial Success',
          description: `Operation completed but failed to refresh bubble data. Please refresh the page.`,
          variant: 'default',
        })
      }
    } else {
      throw new Error(
        result.error ||
          `Failed to ${(localBubble.value as BubbleWithOffline).offline ? 'remove download' : 'download'} bubble`
      )
    }
  } catch (error) {
    logError('Toggle offline error:', error)
    toast({
      title: 'Error',
      description:
        error instanceof Error
          ? error.message
          : `Failed to ${(localBubble.value as BubbleWithOffline)?.offline ? 'remove download' : 'download'} bubble`,
      variant: 'destructive',
    })
  } finally {
    isTogglingOffline.value = false
  }
}

const navigateToInsights = () => {
  router.push(`/bubble/${localBubble.value.uuid}/insights`)
}

// State for async dropdown actions
const isSharing = ref(false)
const isRefreshingMeta = ref(false)
const isTogglingOffline = ref(false)
const isRebuilding = ref(false)
const isTogglingHidden = ref(false)
const isTogglingArchive = ref(false)

const handleShareBubble = async () => {
  if (!localBubble.value) return
  isSharing.value = true
  try {
    const shareData = {
      shareType: 'bubble' as const,
      targetUuid: localBubble.value.uuid,
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: `Bubble "${localBubble.value.title}" can be shared via this link.`,
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link.',
          variant: 'destructive',
        })
      }
    }
  } catch (error) {
    error('Error creating bubble share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing.',
      variant: 'destructive',
    })
  } finally {
    isSharing.value = false
  }
}

// Define dropdown items with loading states
interface DropdownItemAction {
  label: string
  icon: string
  click: () => void
  disabled?: Ref<boolean>
  loading?: Ref<boolean>
  loadingLabel?: string
}

const dropdownItems = computed((): DropdownItemAction[] => {
  const items: DropdownItemAction[] = [
    {
      label: 'Share',
      icon: 'heroicons:share',
      click: handleShareBubble,
      loading: isSharing,
      loadingLabel: 'Sharing...',
      disabled: isSharing,
    },
    {
      label: 'Change Title',
      icon: 'heroicons:pencil',
      click: openTitleChangePopup,
      disabled: isSharing, // Disable other actions while one is loading
    },
    {
      label: 'Refresh',
      icon: 'heroicons:arrow-path',
      click: refreshBubbleMetadata,
      loading: isRefreshingMeta,
      loadingLabel: 'Refreshing...',
      disabled: computed(
        () =>
          isSharing.value ||
          isRefreshingMeta.value ||
          isTogglingOffline.value ||
          isRebuilding.value ||
          isTogglingHidden.value ||
          isTogglingArchive.value ||
          isDeleting.value
      ),
    },
    {
      label: (localBubble.value as BubbleWithOffline).offline ? 'Remove Download' : 'Download',
      icon: (localBubble.value as BubbleWithOffline).offline
        ? 'heroicons:cloud-arrow-up'
        : 'heroicons:cloud-arrow-down',
      click: toggleOffline,
      loading: isTogglingOffline,
      loadingLabel: (localBubble.value as BubbleWithOffline).offline
        ? 'Removing...'
        : 'Downloading...',
      disabled: computed(
        () =>
          isSharing.value ||
          isRefreshingMeta.value ||
          isTogglingOffline.value ||
          isRebuilding.value ||
          isTogglingHidden.value ||
          isTogglingArchive.value ||
          isDeleting.value
      ),
    },
    {
      label: 'Rebuild',
      icon: 'heroicons:cog',
      click: refreshBubbleFull,
      loading: isRebuilding,
      loadingLabel: 'Rebuilding...',
      disabled: computed(
        () =>
          isSharing.value ||
          isRefreshingMeta.value ||
          isTogglingOffline.value ||
          isRebuilding.value ||
          isTogglingHidden.value ||
          isTogglingArchive.value ||
          isDeleting.value
      ),
    },
    {
      label: localBubble.value.hidden ? 'Unhide' : 'Hide',
      icon: localBubble.value.hidden ? 'heroicons:eye' : 'heroicons:eye-slash',
      click: toggleHidden,
      loading: isTogglingHidden,
      loadingLabel: localBubble.value.hidden ? 'Unhiding...' : 'Hiding...',
      disabled: computed(
        () =>
          isSharing.value ||
          isRefreshingMeta.value ||
          isTogglingOffline.value ||
          isRebuilding.value ||
          isTogglingHidden.value ||
          isTogglingArchive.value ||
          isDeleting.value
      ),
    },
    {
      label: localBubble.value.archived ? 'Unarchive' : 'Archive',
      icon: localBubble.value.archived
        ? 'heroicons:archive-box-arrow-down'
        : 'heroicons:archive-box',
      click: toggleArchive,
      loading: isTogglingArchive,
      loadingLabel: localBubble.value.archived ? 'Unarchiving...' : 'Archiving...',
      disabled: computed(
        () =>
          isSharing.value ||
          isRefreshingMeta.value ||
          isTogglingOffline.value ||
          isRebuilding.value ||
          isTogglingHidden.value ||
          isTogglingArchive.value ||
          isDeleting.value
      ),
    },
    {
      label: 'Delete',
      icon: 'heroicons:trash',
      click: deleteBubble,
      loading: isDeleting,
      loadingLabel: 'Deleting...',
      disabled: computed(
        () =>
          isSharing.value ||
          isRefreshingMeta.value ||
          isTogglingOffline.value ||
          isRebuilding.value ||
          isTogglingHidden.value ||
          isTogglingArchive.value ||
          isDeleting.value
      ),
    },
  ]

  if ('url' in localBubble.value && localBubble.value.url) {
    items.unshift({
      label: 'Open URL',
      icon: 'heroicons:link',
      click: openUrl,
      disabled: isSharing, // Disable while sharing
    })
  }

  return items
})

const showChat = ref(false)

const openChatModal = () => {
  showChat.value = true
}

const isTitleChangePopupOpen = ref(false)

const openTitleChangePopup = () => {
  isTitleChangePopupOpen.value = true
}

const isNoteModalOpen = ref(false)

const openNoteModal = () => {
  isNoteModalOpen.value = true
}

const closeNoteModal = () => {
  isNoteModalOpen.value = false
}

const handleTitleChanged = async (newTitle: string) => {
  try {
    const result = await bubbleStore.changeBubbleTitle(localBubble.value.uuid, newTitle)
    if (result.success) {
      // Emit an event to update the parent component
      emit('update:bubble', { ...localBubble.value, title: newTitle })
      toast({
        title: 'Success',
        description: 'Bubble title updated successfully',
      })
    } else {
      throw new Error(result.error || 'Failed to update bubble title')
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to update bubble title',
      variant: 'destructive',
    })
  }
}

const openUrl = () => {
  if ('url' in props.bubble && props.bubble.url) {
    window.open(props.bubble.url, '_blank')
  }
}
</script>
